import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, ImageIcon, X, Truck } from 'lucide-react';
const allReviews = [{
  name: 'Michael T.',
  role: 'Homeowner',
  location: 'Tacoma, WA',
  content: 'On time and easy to work with. I needed 5 yards of crushed rock for a driveway extension. They gave me a quote instantly and delivered it the next morning.',
  photo: 'https://cdn.magicpatterns.com/uploads/jqPpSAL3shmbQTsuBx2p1g/Screenshot_20260507-193826.png',
  materialUsed: '5/8" Minus',
  zone: 'Zone 3'
}, {
  name: 'Sarah J.',
  role: 'Landscaping Contractor',
  location: 'Puyallup, WA',
  content: 'Best pricing in the Seattle-Tacoma area. We use 253Gravel for all our King County projects. They actually show up when they say they will.'
}, {
  name: 'David W.',
  role: 'Homeowner',
  location: 'Federal Way, WA',
  content: 'Delivered exactly where I needed it. The material calculator was super helpful—I ordered exactly what I needed for my patio base. Great quality road base.'
}, {
  name: 'Karen L.',
  role: 'Homeowner',
  location: 'Lakewood, WA',
  content: 'Ordered a big load of fill dirt for a grading project at our new build. Driver dumped it exactly where we needed it next to the foundation. Saved us a ton of time and the price beat every other quote we got.',
  photo: 'https://cdn.magicpatterns.com/uploads/4nanabaojFe9Pp7KUyTgfL/PXL_20260508_215909413.jpg',
  materialUsed: 'Fill Dirt',
  zone: 'Zone 4'
}, {
  name: 'James R.',
  role: 'General Contractor',
  location: 'Spanaway, WA',
  content: "Reliable and consistent quality. We've used them on three different job sites this year. The online ordering makes it easy to schedule around our project timelines."
}, {
  name: 'Lisa M.',
  role: 'Homeowner',
  location: 'University Place, WA',
  content: 'I had no idea how much gravel I needed, but their calculator nailed it. Got the 5/8 minus for my walkway and it looks amazing. Super fast delivery too.'
}, {
  name: 'Tom H.',
  role: 'Property Manager',
  location: 'Tacoma, WA',
  content: 'We manage several rental properties and use 253Gravel for all our gravel and bark needs. Pricing is transparent and the delivery guys are always professional.',
  photo: 'https://cdn.magicpatterns.com/uploads/842UsR3B2N459pNY6iLg3A/PXL_20260430_160334850~2.jpg',
  materialUsed: '3-Way Topsoil',
  zone: 'Zone 3'
}, {
  name: 'Amanda P.',
  role: 'Homeowner',
  location: 'Bonney Lake, WA',
  content: 'Ordered 10 yards of beauty bark for our front and back yards. It was delivered within 24 hours and the quality was way better than the big box stores.'
}, {
  name: 'Chris D.',
  role: 'Landscaping Contractor',
  location: 'Graham, WA',
  content: "The bulk discount on bark saved us a ton on a large commercial project. Communication was great from quote to delivery. These guys know what they're doing."
}, {
  name: 'Rachel K.',
  role: 'Homeowner',
  location: 'Sumner, WA',
  content: "First time ordering bulk material and they made it so easy. The driver called ahead and placed it exactly where I asked. Couldn't be happier with the 1.25 clear.",
  photo: 'https://cdn.magicpatterns.com/uploads/pqpwYUSWGeueYo493Urzhf/Screenshot_20260507-193040.png',
  materialUsed: '1.25" Clean',
  zone: 'Zone 3'
}, {
  name: 'Brian S.',
  role: 'Excavation Contractor',
  location: 'Eatonville, WA',
  content: "We go through a lot of quarry rock and drain rock. 253Gravel has been the most consistent supplier we've worked with in Pierce County. Fair prices, no games."
}, {
  name: 'Nicole F.',
  role: 'Homeowner',
  location: 'Gig Harbor, WA',
  content: 'Needed gravel for a French drain project. They helped me pick the right size and delivered it the same week. The whole process from quote to delivery was seamless.',
  photo: 'https://cdn.magicpatterns.com/uploads/oTDFMkKEU78tjXHJCNVnma/Screenshot_20260507-195144.png',
  materialUsed: '7/8" Drain Rock',
  zone: 'Zone 4'
}, {
  name: 'Marcus B.',
  role: 'Site Superintendent',
  location: 'Auburn, WA',
  content: "Needed 50 yards of 5/8 minus dropped at a tight commercial pad on 24-hour notice. Two trucks, both on time, driver placed it exactly on the stake. That's the kind of reliability that keeps our crews moving.",
  materialUsed: '5/8" Minus',
  volume: '50 yards',
  zone: 'Zone 4'
}, {
  name: 'Derek O.',
  role: 'Excavation Contractor',
  location: 'Kent, WA',
  content: 'Ordered 75 yards of quarry spalls for a retaining wall base — three loads back-to-back, dispatched in under an hour. Pricing beat our usual supplier by close to a grand on this one job alone.',
  materialUsed: 'Quarry Spalls',
  volume: '75 yards',
  zone: 'Zone 5'
}, {
  name: 'Anthony G.',
  role: 'GC / Custom Homes',
  location: 'Maple Valley, WA',
  content: "30 yards of 3-way topsoil for a new build's final grade — texted the order at 6:45am, first truck rolled in by 9. Net-30 billing makes my accountant happy too.",
  materialUsed: '3-Way Topsoil',
  volume: '30 yards',
  zone: 'Zone 5'
}];
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
const REVIEWS_PER_PAGE = 3;
export function Reviews() {
  const shuffledReviews = useMemo(() => shuffleArray(allReviews), []);
  const totalPages = Math.ceil(shuffledReviews.length / REVIEWS_PER_PAGE);
  const [page, setPage] = useState(0);
  const currentReviews = shuffledReviews.slice(page * REVIEWS_PER_PAGE, (page + 1) * REVIEWS_PER_PAGE);
  const [lightboxPhoto, setLightboxPhoto] = useState<{
    src: string;
    alt: string;
    material?: string;
    zone?: string;
  } | null>(null);
  return <section className="relative py-28 dark:bg-[#333231] transition-colors duration-300 border-y border-stone-200 dark:border-[#574f4a] bg-[#FCF9F2]" id="reviews" data-id="element-1118">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1119">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6" data-id="element-1120">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }} data-id="element-1121">
            <p className="text-sm font-bold text-brand-dim dark:text-brand-muted uppercase tracking-widest mb-3" data-id="element-1122">
              Testimonials
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-extrabold text-stone-900 dark:text-stone-200 tracking-tight" data-id="element-1123">
              Trusted by the 253
            </h2>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1],
          delay: 0.1
        }} className="flex items-center gap-3" data-id="element-1124">
            <div className="flex -space-x-0.5" data-id="element-1125">
              {[1, 2, 3, 4, 5].map(star => <Star key={star} className="w-5 h-5 fill-[#d4a017] text-[#d4a017]" data-id="element-1126" />)}
            </div>
            <span className="text-stone-500 dark:text-stone-400 text-sm font-medium" data-id="element-1127">
              4.9 from 250+ reviews
            </span>
          </motion.div>
        </div>

        {/* Reviews Grid */}
        <AnimatePresence mode="wait" data-id="element-1128">
          <motion.div key={page} initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -30
        }} transition={{
          duration: 0.35,
          ease: [0.16, 1, 0.3, 1]
        }} className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-1129">
            {currentReviews.map((review, index) => <motion.div key={review.name} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: index * 0.08,
            ease: [0.16, 1, 0.3, 1]
          }} className="dark:bg-[#3c3b3b] p-8 rounded-2xl border border-stone-200 dark:border-[#4a4848] flex flex-col h-full shadow-sm dark:shadow-none bg-[#FBF6EF] relative" data-id="element-1130">
                {/* Volume badge — for large contractor orders */}
                {review.volume && <div className="absolute -top-3 right-6 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-stone-900 dark:bg-[#fbbf24] text-white dark:text-stone-900 text-[11px] font-extrabold tracking-wider uppercase shadow-md" data-id="element-1131">
                    <Truck className="w-3 h-3" data-id="element-1132" />
                    {review.volume}
                  </div>}
                <div className="flex gap-1 mb-6" data-id="element-1133">
                  {[1, 2, 3, 4, 5].map(star => <Star key={star} className="w-4 h-4 fill-[#d4a017] text-[#d4a017]" data-id="element-1134" />)}
                </div>
                <p className="text-stone-700 dark:text-stone-300 leading-relaxed mb-4 flex-grow text-[15px]" data-id="element-1135">
                  "{review.content}"
                </p>
                {review.photo && <button type="button" onClick={() => setLightboxPhoto({
              src: review.photo!,
              alt: `Project by ${review.name}`,
              material: review.materialUsed,
              zone: review.zone
            })} className="inline-flex items-center gap-1.5 text-xs font-bold hover:text-brand-orangeHover transition-colors mb-6 self-start text-[#738979]" data-id="element-1136">
                    <ImageIcon className="w-3.5 h-3.5" data-id="element-1137" />
                    View photo
                    {review.materialUsed && <span className="text-stone-500 dark:text-stone-400 font-medium ml-1" data-id="element-1138">
                        · {review.materialUsed}
                      </span>}
                  </button>}
                {!review.photo && review.materialUsed && <div className="inline-flex items-center gap-1.5 text-xs font-bold text-stone-500 dark:text-stone-400 mb-6 self-start" data-id="element-1139">
                    <span className="font-medium" data-id="element-1140">
                      Material: {review.materialUsed}
                    </span>
                  </div>}
                <div className="flex items-center gap-4 mt-auto pt-6 border-t border-stone-500 dark:border-[#323335]" data-id="element-1141">
                  <div className="w-10 h-10 rounded-full bg-stone-400 dark:bg-[#262829] flex items-center justify-center text-stone-700 dark:text-stone-200 font-bold font-heading" data-id="element-1142">
                    {review.name.charAt(0)}
                  </div>
                  <div data-id="element-1143">
                    <p className="text-stone-900 dark:text-stone-200 font-bold text-sm" data-id="element-1144">
                      {review.name}
                    </p>
                    <p className="text-stone-500 dark:text-stone-500 text-xs" data-id="element-1145">
                      {review.role} • {review.location}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </motion.div>
        </AnimatePresence>

        {/* Pagination */}
        <div className="flex items-center justify-center gap-4 mt-10" data-id="element-1146">
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="p-2 rounded-full border border-stone-600 dark:border-[#3a3b3e] text-stone-800 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-200 hover:border-stone-700 dark:hover:border-[#4b4c4f] transition-colors disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-1147">
            <ChevronLeft className="w-5 h-5" data-id="element-1148" />
          </button>

          <div className="flex gap-2" data-id="element-1149">
            {Array.from({
            length: totalPages
          }).map((_, i) => <button key={i} onClick={() => setPage(i)} className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${i === page ? 'bg-brand-orange scale-110' : 'bg-stone-300 dark:bg-stone-500 hover:bg-stone-400 dark:hover:bg-stone-400'}`} data-id="element-1150" />)}
          </div>

          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page === totalPages - 1} className="p-2 rounded-full border border-stone-600 dark:border-[#3a3b3e] text-stone-800 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-200 hover:border-stone-700 dark:hover:border-[#4b4c4f] transition-colors disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-1151">
            <ChevronRight className="w-5 h-5" data-id="element-1152" />
          </button>
        </div>
      </div>

      {/* Photo Lightbox */}
      <AnimatePresence data-id="element-1153">
        {lightboxPhoto && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={() => setLightboxPhoto(null)} className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 cursor-zoom-out" data-id="element-1154">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} transition={{
          duration: 0.25,
          ease: [0.16, 1, 0.3, 1]
        }} onClick={e => e.stopPropagation()} className="relative max-w-4xl w-full cursor-default" data-id="element-1155">
              <button type="button" onClick={() => setLightboxPhoto(null)} className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md flex items-center justify-center text-white transition-colors" aria-label="Close photo" data-id="element-1156">
                <X className="w-5 h-5" data-id="element-1157" />
              </button>
              <img src={lightboxPhoto.src} alt={lightboxPhoto.alt} className="w-full h-auto max-h-[85vh] object-contain rounded-2xl shadow-2xl" data-id="element-1158" />
              {(lightboxPhoto.material || lightboxPhoto.zone) && <div className="absolute bottom-4 left-4 flex gap-2" data-id="element-1159">
                  {lightboxPhoto.material && <span className="bg-black/70 backdrop-blur-md text-white text-xs font-bold px-3 py-1.5 rounded-md" data-id="element-1160">
                      {lightboxPhoto.material}
                    </span>}
                  {lightboxPhoto.zone && <span className="bg-brand-orange/90 backdrop-blur-md text-white text-xs font-bold px-3 py-1.5 rounded-md" data-id="element-1161">
                      {lightboxPhoto.zone}
                    </span>}
                </div>}
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}